import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { IconButton, Stack } from '@mui/material';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import { HeaderAndContainer, Loader, Tooltip } from '@trucktrax/trucktrax-common';
import { PlantDto } from '@trucktrax/trucktrax-ts-common';
import { getIncomingMaterialsPositions, getMapview } from '../../../services/mapviewsService';
import { getPlantList } from '../../../services/plantsService';
import { getUserPreferences } from '../../../services/userPreferencesService';
import { setIncomingPlantList } from '../../../store/actions/plantsActions';
import { setIncomingPositionList } from '../../../store/actions/mapviewActions';
import { toggleMapFilter } from '../../../store/actions/mapFilterActions';
import { getIdFromUrl, setFavicon } from '../../../util/appUtil';
import {
  buildIncomingPlantFilters,
  readIncomingDeselectedPlantIds,
  readIncomingDeselectedProductLines,
} from '../../../util/incomingMaterialsPreferencesUtil';
import { isFeatureFlagEnabled } from '../../../util/featureFlagUtil';
import {
  INCOMING_MATERIALS_FLAG,
  INCOMING_TICKETS_SELECTED,
  SUCCESS,
  TOOLTIP_SET_DEFAULT_MAPVIEW,
  TOOLTIP_REMOVE_DEFAULT_MAPVIEW,
} from '../../../constants/appConstants';
import MapFiltersDropdown from '../map/MapFilters';
import { DEFAULT_MAP_CENTER } from '../../../constants/mapConstants';
import { INCOMING_MATERIALS_USER_OVERRIDE } from '../../../constants/localStorageConstants';
import { ReduxState } from '../../../store';
import { openSnackbar } from '../../../store/actions/snackbarActions';
import { isValidLatLng } from '../../../util/geoUtil';
import {
  ADD_USER_DEFAULT_LOCATION,
  REMOVE_USER_DEFAULT_LOCATION,
} from '../../../constants/successConstants';
import VehicleAndOrderSearch from '../../shared/header/VehicleAndOrderSearch';
import IncomingMaterialsMapContainer from './IncomingMaterialsMapContainer';
import { MapMarker, PositionDetailsDto, SidebarTitleType } from '../../../types';
import incomingActiveMarkersSelector, {
  hasArrivedIncomingTicketsSelector,
  incomingPlantProductLinesSelector,
} from '../../../store/selectors/incomingMaterialsSelectors';
import setSidebarSelected from '../../../store/actions/sidebarActions';
import styles from './IncomingMaterialsView.module.scss';

const DEFAULT_INCOMING_ZOOM = 7;

interface IncomingMaterialsOverride {
  lat: number;
  lng: number;
  zoom: number;
}

type OverrideMap = Record<string, IncomingMaterialsOverride>;

function loadAllOverrides(): OverrideMap {
  try {
    const stored = localStorage.getItem(INCOMING_MATERIALS_USER_OVERRIDE);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function loadOverride(regionUrl: string): IncomingMaterialsOverride | null {
  const all = loadAllOverrides();
  return all[regionUrl] ?? null;
}

function saveOverride(regionUrl: string, override: IncomingMaterialsOverride) {
  const all = loadAllOverrides();
  all[regionUrl] = override;
  localStorage.setItem(INCOMING_MATERIALS_USER_OVERRIDE, JSON.stringify(all));
}

function removeOverride(regionUrl: string) {
  const all = loadAllOverrides();
  delete all[regionUrl];
  localStorage.setItem(INCOMING_MATERIALS_USER_OVERRIDE, JSON.stringify(all));
}

// DEV-5225: the Product Line filter before it is hydrated for a region: all checked, and
// neither verified nor edited, so no save writes that default over the saved selection.
const UNVERIFIED_PRODUCT_LINES = {
  incomingDeselectedProductLines: [],
  incomingProductLinesVerified: false,
  incomingProductLinesEdited: false,
};

interface IncomingMaterialsViewProps extends RouteComponentProps {
  userPermission: ReduxState['userPermission'];
  currentRegion: ReduxState['currentRegion'];
  userUrl: ReduxState['userUrl'];
  /** Comma-joined user product lines for getPlantList's inProductLines; '' when none. */
  plantProductLines: string;
  incomingMarkers: MapMarker[];
  hasArrivedTickets: boolean;
  /** DEV-5225: the ticket panel's Product Line filter matches the saved selection. */
  incomingProductLinesVerified?: boolean;
  /** DEV-5225: the user changed the Product Line filter since the last save. */
  incomingProductLinesEdited?: boolean;
  openSnackbar: typeof openSnackbar;
  getIncomingMaterialsPositions: (destinationPlants: string[]) => void;
  getMapview: (regionUrl?: string, force?: boolean) => void;
  setIncomingPlantList: (plants: PlantDto[]) => void;
  setIncomingPositionList: (positions: PositionDetailsDto[]) => void;
  toggleMapFilter: typeof toggleMapFilter;
  setSidebarSelected: (sidebarTitle: SidebarTitleType, isSelected: boolean) => void;
}

interface IncomingMaterialsViewState {
  lat: number;
  lng: number;
  zoom: number;
  hasOverride: boolean;
}

export class IncomingMaterialsView extends Component<
  IncomingMaterialsViewProps,
  IncomingMaterialsViewState
> {
  // Guards the async loadIncomingData dispatches against unmount races. The
  // region-drift check covers a region change mid-flight, but a plain
  // unmount-during-fetch would otherwise resurrect the slices that
  // componentWillUnmount cleared.
  mounted = false;

  constructor(props: IncomingMaterialsViewProps) {
    super(props);

    const regionUrl = props.currentRegion?.url;
    const override = regionUrl ? loadOverride(regionUrl) : null;
    const regionLat = props.currentRegion?.location?.latitude ?? DEFAULT_MAP_CENTER.lat;
    const regionLng = props.currentRegion?.location?.longitude ?? DEFAULT_MAP_CENTER.lng;

    if (override && isValidLatLng(override.lat, override.lng)) {
      this.state = {
        lat: override.lat,
        lng: override.lng,
        zoom: override.zoom,
        hasOverride: true,
      };
    } else {
      this.state = {
        lat: regionLat,
        lng: regionLng,
        zoom: DEFAULT_INCOMING_ZOOM,
        hasOverride: false,
      };
    }
  }

  componentDidMount() {
    this.mounted = true;
    document.title = 'GeoTrax - TruckTrax';
    setFavicon('/favicon-geotrax.ico');
    // Intentionally do NOT touch selectedProductLine here. The IM page once
    // depended on the standard map's positionList, which required the user's
    // product line to be ReadyMix; the previous fix forced that. Now IM has
    // its own incomingPositionList + getIncomingMaterialsPositions (filtered
    // server-side by destinationPlants, not productLine), so mutating
    // selectedProductLine on mount would only clobber the user's prior
    // selection — see DEV-3867.
    this.enforceFeatureFlag();
    this.loadIncomingData();
    // IM ticket reporting on the socket is owned app-wide by
    // IncomingMaterialsSubscription (DEV-4154) — nothing to toggle here.
    //
    // DEV-4154: arriving on the page while the arrival notification is active
    // auto-opens the incoming tickets panel so the flagged tickets are
    // immediately in view. Mount-time only — a truck arriving while the user
    // is already on the page must not yank the panel open.
    if (this.props.hasArrivedTickets) {
      this.props.setSidebarSelected(INCOMING_TICKETS_SELECTED, true);
    }
  }

  componentDidUpdate(prevProps: IncomingMaterialsViewProps) {
    if (
      prevProps.currentRegion?.url !== this.props.currentRegion?.url ||
      prevProps.userPermission !== this.props.userPermission
    ) {
      this.enforceFeatureFlag();
    }

    if (prevProps.currentRegion?.url !== this.props.currentRegion?.url) {
      this.applyRegionCenter();
      // Region changed — clear stale positions/plants so they cannot leak
      // across regions while the new fetch is in flight. DEV-4973: drop any stuck or
      // in-flight save flag first, so the plant-list clear cannot fire a preferences save
      // under the new region before its selection is hydrated.
      // DEV-5225: also drop the previous region's Product Line filter; unverified, its
      // all-checked default is not saved over the new region's selection.
      this.props.toggleMapFilter({ shouldSave: false, ...UNVERIFIED_PRODUCT_LINES });
      this.props.setIncomingPositionList([]);
      this.props.setIncomingPlantList([]);
      this.loadIncomingData();
      // Ticket subscription handover on region change is owned by
      // IncomingMaterialsSubscription (DEV-4154).
    } else if (prevProps.plantProductLines !== this.props.plantProductLines) {
      // The user's product lines can arrive after mount (App dispatches them from the token on its own mount).
      this.loadIncomingData();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.props.setIncomingPositionList([]);
    this.props.setIncomingPlantList([]);
    // DEV-5225: the region can change while the user is away, so the Product Line filter is
    // unverified again until the next visit hydrates it.
    this.props.toggleMapFilter(UNVERIFIED_PRODUCT_LINES);
    // App bootstrap (regionsService) skips the standard /mapviews/details fetch
    // while the user is on /incomingMaterials, leaving positionList empty.
    // Refill it now so the next /geotrax/map view has positions to render
    // instead of waiting for socket heartbeats.
    this.props.getMapview(this.props.currentRegion?.url, true);
  }

  applyRegionCenter() {
    const { currentRegion } = this.props;
    const override = currentRegion?.url ? loadOverride(currentRegion.url) : null;

    if (override && isValidLatLng(override.lat, override.lng)) {
      this.setState({
        lat: override.lat,
        lng: override.lng,
        zoom: override.zoom,
        hasOverride: true,
      });
    } else {
      this.setState({
        lat: currentRegion?.location?.latitude ?? DEFAULT_MAP_CENTER.lat,
        lng: currentRegion?.location?.longitude ?? DEFAULT_MAP_CENTER.lng,
        zoom: DEFAULT_INCOMING_ZOOM,
        hasOverride: false,
      });
    }
  }

  loadIncomingData = async () => {
    const regionUrl = this.props.currentRegion?.url;
    if (!regionUrl) return;
    const requestedRegion = regionUrl;
    const { plantProductLines } = this.props;
    if (!plantProductLines) {
      // No user product lines (or not loaded yet): no plants in scope. An unfiltered
      // getPlantList would return every product line's plants.
      this.props.setIncomingPlantList([]);
      return;
    }
    const { userUrl } = this.props;
    const preferencesRequest = userUrl ? getUserPreferences(userUrl) : Promise.resolve(undefined);
    let plants: PlantDto[] = [];
    try {
      plants =
        (await getPlantList(regionUrl, undefined, undefined, undefined, plantProductLines)) ?? [];
    } catch {
      // A transient plants failure isn't "no plants in scope" — keep the existing plant
      // list and its markers rather than blanking them and dropping the position refresh.
      // Mirrors IncomingMaterialsSubscription.seed; the next region change or refocus
      // reseeds. (getPlantList propagates again as of the DEV-4427 follow-up; see DEV-4154
      // for why conflating the two is the bug.)
      return;
    }
    const preferences = await preferencesRequest;
    // Drop the response if the component unmounted while the fetch was in
    // flight — otherwise the dispatches below would resurrect the slices
    // that componentWillUnmount just cleared. Also bail if the region or product lines drifted.
    if (!this.mounted) return;
    if (this.props.currentRegion?.url !== requestedRegion) return;
    if (this.props.plantProductLines !== plantProductLines) return;
    // DEV-4973: shouldSave is only cleared after a successful PUT, so it can still be set
    // here; clear it with the hydration so a failed read's all-checked fallback is never
    // merged over the saved selection.
    this.props.toggleMapFilter({
      incomingPlantFilters: buildIncomingPlantFilters(
        plants,
        readIncomingDeselectedPlantIds(preferences, getIdFromUrl(regionUrl)),
      ),
      shouldSave: false,
      // DEV-4973: a failed (or skipped, no user url) read shows every plant checked whatever is
      // saved. Flag it so the next save holds back the selection write until a read succeeds;
      // clearing shouldSave alone does not cover a genuine click.
      incomingPlantFiltersUnverified: preferences === undefined,
      // DEV-5225: the ticket panel's Product Line filter, saved in the same region entry. These
      // preferences were read when loading started, so an edit made since, or a save that
      // verified the filter since, is newer and is left alone.
      ...(this.props.incomingProductLinesEdited || this.props.incomingProductLinesVerified
        ? {}
        : {
            incomingDeselectedProductLines: readIncomingDeselectedProductLines(
              preferences,
              getIdFromUrl(regionUrl),
            ),
            incomingProductLinesVerified: preferences !== undefined,
          }),
    });
    this.props.setIncomingPlantList(plants);
    const destinationPlants = plants.map(p => p.url).filter((url): url is string => !!url);
    if (destinationPlants.length === 0) return;
    this.props.getIncomingMaterialsPositions(destinationPlants);
  };

  enforceFeatureFlag() {
    const regionUrl = this.props.currentRegion?.url;
    if (!regionUrl) {
      return;
    }

    // Don't redirect until permissions have loaded for this region
    if (!this.props.userPermission[regionUrl]) {
      return;
    }

    const isEnabled = isFeatureFlagEnabled(
      this.props.userPermission,
      regionUrl,
      INCOMING_MATERIALS_FLAG,
    );
    if (!isEnabled) {
      this.props.history.replace('/geotrax/map');
    }
  }

  handleCenterChanged = (center: { lat: number; lng: number }) => {
    if (this.state.lat !== center.lat || this.state.lng !== center.lng) {
      this.setState({ lat: center.lat, lng: center.lng });
    }
  };

  handleZoomChanged = (zoom: number) => {
    if (this.state.zoom !== zoom) {
      this.setState({ zoom });
    }
  };

  toggleDefaultLocation = () => {
    const { lat, lng, zoom, hasOverride } = this.state;
    const regionUrl = this.props.currentRegion?.url;
    if (!regionUrl) return;

    if (hasOverride) {
      removeOverride(regionUrl);
      this.setState({ hasOverride: false });
      this.props.openSnackbar({
        snackbarBody: REMOVE_USER_DEFAULT_LOCATION,
        dataTest: 'home-location-removed',
        snackbarType: SUCCESS,
      });
      return;
    }

    if (!isValidLatLng(lat, lng)) return;

    saveOverride(regionUrl, { lat, lng, zoom });
    this.setState({ hasOverride: true });
    this.props.openSnackbar({
      snackbarBody: ADD_USER_DEFAULT_LOCATION,
      dataTest: 'home-location-success',
      snackbarType: SUCCESS,
    });
  };

  render() {
    const { currentRegion } = this.props;

    if (currentRegion?.name === undefined) {
      return <Loader />;
    }

    const { lat, lng, zoom, hasOverride } = this.state;
    const center = { lat, lng };

    const elementsLeft = (
      <div className={styles.flexGroup}>
        <MapFiltersDropdown variant="incoming" />
        <h5 className={styles.h5}>Incoming Materials</h5>
      </div>
    );

    const elementsRight = (
      <Stack direction="row">
        <VehicleAndOrderSearch markers={this.props.incomingMarkers} />
        <Tooltip text={hasOverride ? TOOLTIP_REMOVE_DEFAULT_MAPVIEW : TOOLTIP_SET_DEFAULT_MAPVIEW}>
          <IconButton
            onClick={this.toggleDefaultLocation}
            data-test="incoming-home-location-button"
          >
            <AddLocationIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    );

    return (
      <HeaderAndContainer
        elementsLeft={elementsLeft}
        elementsRight={elementsRight}
        containerComponent={
          <IncomingMaterialsMapContainer
            center={center}
            zoom={zoom}
            onCenterChanged={this.handleCenterChanged}
            onZoomChanged={this.handleZoomChanged}
          />
        }
      />
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    userPermission: state.userPermission || {},
    currentRegion: state.currentRegion,
    userUrl: state.userUrl ?? '',
    plantProductLines: incomingPlantProductLinesSelector(state),
    incomingMarkers: incomingActiveMarkersSelector(state),
    hasArrivedTickets: hasArrivedIncomingTicketsSelector(state),
    incomingProductLinesVerified: state.mapFilters?.incomingProductLinesVerified,
    incomingProductLinesEdited: state.mapFilters?.incomingProductLinesEdited,
  };
}

export default withRouter(
  connect(mapStateToProps, {
    openSnackbar,
    getIncomingMaterialsPositions,
    getMapview,
    setIncomingPlantList,
    setIncomingPositionList,
    toggleMapFilter,
    setSidebarSelected,
  })(IncomingMaterialsView) as any,
);
